// @flow

import React from 'react';
import type { Node } from 'react';
import styled, { css } from 'styled-components';

import {
  absolute,
  alignByBreakpoint,
  blockypDefault,
  Color,
  flex,
  h1Default,
  h28,
  maxWidths,
  topMargin,
  Width,
  Fonts,
  breakLine,
} from 'styles';
import { graphql, useStaticQuery } from 'gatsby';
import { useImages } from 'hooks';
import { ImageLoader } from 'components/Gui';

const Wrapper = styled.div`
  ${({ marginTop }) => marginTop && topMargin(...marginTop)};
  ${flex()};
  flex-direction: column;
  ${({ alignContent }) => alignContent && alignByBreakpoint(...alignContent)};

  p {
    ${blockypDefault};
    color: ${Color.GREY_DARK};
  }

  .h1 {
    ${h1Default};
  }

  h3,
  .h3 {
    ${Fonts.GOTHIC};
    font-weight: 400;
    line-height: calc(34 / 24);
    font-size: 2.4rem;
    margin-bottom: 24px;
  }

  .h5 {
    ${Fonts.GOTHIC};
    font-size: 1.8rem;
    letter-spacing: 0.015em;
    font-weight: 500;
    line-height: 1.55;
    margin-bottom: 16px;
  }

  h2 {
    ${h28};
  }

  > div {
    width: 100%;
  }

  > div.text-header {
    ${({ hMaxWidths }) => hMaxWidths && maxWidths(...hMaxWidths)};
    ${({ hDotsWrapper }) =>
      hDotsWrapper &&
      css`
        height: 216px;
        position: relative;
        ${flex()};
        z-index: 1;

        @media print, screen and (min-width: ${Width.BREAK_MD}px) {
          box-sizing: border-box;
          padding: 0 40px;
        }
      `};
    * {
      margin-bottom: 0;
      ${({ hBreakLine }) => hBreakLine && breakLine(...hBreakLine)};
    }
  }

  > div.text-body {
    ${({ hasHeader }) => hasHeader && topMargin(24, 24, 24)}
    ${({ bMaxWidths }) => bMaxWidths && maxWidths(...bMaxWidths)};
  }

  div.buttons {
    display: inline-block;
    ${({ buttonTopMargins }) => topMargin(...buttonTopMargins)};
  }

  div.trustpilot-summary {
    ${topMargin(24, 30, 30)};
  }
`;

const Squares = styled.div`
  z-index: -1;
  ${absolute()};
  ${flex()};

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    justify-content: flex-end;
  }

  > div {
    width: 202px;
  }
`;

type TextConfig = {
  maxWidths?: number[],
  content: Node | string,
  dotsWrapper?: boolean,
  breakLine?: boolean[],
};

type Props = {
  marginTop?: number[],
  alignContent?: string[],
  header?: TextConfig,
  body?: TextConfig,
  buttonTopMargins?: number,
};

const BlockyText = ({
  marginTop,
  alignContent,
  header: { maxWidths: hMaxWidths, content: hContent, dotsWrapper: hDotsWrapper, breakLine: hBreakLine } = {},
  body: { maxWidths: bMaxWidths, content: bContent } = {},
  buttonTopMargins = [24],
  ...rest
}: Props) => {
  const squaresImages = useStaticQuery(graphql`
    query {
      otherImage: allFile(filter: { relativeDirectory: { eq: "global/blocky" }, extension: { in: ["svg", "gif"] } }) {
        edges {
          node {
            ...OtherImage
          }
        }
      }
    }
  `);
  const getSquaresImage = useImages(squaresImages);
  return (
    <Wrapper
      alignContent={alignContent}
      marginTop={marginTop}
      hasHeader={!!hContent}
      hMaxWidths={hMaxWidths}
      bMaxWidths={bMaxWidths}
      hBreakLine={hBreakLine}
      buttonTopMargins={buttonTopMargins}
      hDotsWrapper={hDotsWrapper}
      {...rest}>
      {hContent && (
        <div className="text-header">
          {hDotsWrapper && (
            <Squares>
              <div>
                <ImageLoader image={getSquaresImage('squares-large')} />
              </div>
            </Squares>
          )}
          {hContent}
        </div>
      )}
      {bContent && <div className="text-body">{bContent}</div>}
    </Wrapper>
  );
};

BlockyText.contentType = 'BlockyText';

export default BlockyText;
